import { ONBOARDED_FEED_QUEST_ID } from "@/constants";
import { notifyError } from "@/utils/monitoring/newrelic";
import type {
  BaseFieldData,
  CTAFieldData,
  DiscoverFeedItem,
  FollowGameFieldData,
  InGameQuestFieldData,
  QuizFieldData,
  RewardFieldData,
  SocialQuestFieldData,
  VideoFieldData,
} from "./Discover";
import { QuizHeaderToCMSType } from "./Discover";
import { VideoUrl } from "./VideoUrl";

type FieldDataMap = {
  video: VideoFieldData;
  quiz: QuizFieldData;
  cta: CTAFieldData;
  followgame: FollowGameFieldData;
  reward: RewardFieldData;
  onboardingsignin: RewardFieldData;
  pintohome: BaseFieldData;
  bonusgems: BaseFieldData;
  referral: BaseFieldData;
  getrewardsready: BaseFieldData;
  socialquest: SocialQuestFieldData;
  allcaughtup: never;
  selectquestdifficulty: never;
  socialconnection: never;
  signin: never;
  selectgenre: never;
  ingamequest: InGameQuestFieldData;
};

type TemplateTypes = keyof FieldDataMap;

type TemplateConvertersMap = {
  [K in keyof FieldDataMap]: (data: FieldDataMap[K]) => DiscoverFeedItem;
};

const toBaseTemplate = (fieldData: BaseFieldData) => {
  // Favor quest-id over slug when available
  const questId = fieldData["quest-id"] || fieldData.slug;

  return {
    id: fieldData.slug,
    questCompletePopupText: fieldData["quest-complete-popup-text"],
    quest: {
      questId: questId,
      gemsEarnable: fieldData["gems-earnable-2"],
    },
    bypass: fieldData.bypass,
    experimentId: fieldData["experiment-id-2"],
    experimentVariant: fieldData["experiment-variant"],
    onboardingExperience: fieldData["onboarding-experience"],
    priority: fieldData.priority,
    categories: fieldData.categories
      ? fieldData.categories.split(",").map((cat) => cat.trim().toLowerCase())
      : [],
    tags: fieldData.tags
      ? fieldData.tags.split(",").map((tag) => tag.trim().toLowerCase())
      : [],
    controlTags: fieldData["control-tags"]
      ? fieldData["control-tags"]
          .split(",")
          .map((tag) => tag.trim().toLowerCase())
      : [],
    createdAt: fieldData.createdAt,
  };
};

const toRewardTemplate = (
  fieldData: RewardFieldData,
): DiscoverFeedItem | undefined => {
  // DISABLED TO ENABLE EXPERIMENTATION ON REWARD TEMPLATE
  // if (!fieldData["reward-background"]) {
  //   notifyError(
  //     "Reward template is missing required field: reward-background",
  //     "discoverFeedError",
  //   );
  //   return undefined;
  // }

  return {
    ...toBaseTemplate(fieldData),
    type: "reward",
    quest: {
      questId: `${ONBOARDED_FEED_QUEST_ID}-event`,
      gemsEarnable: fieldData["gems-earnable-2"],
    },
    rewardBackground: fieldData["reward-background"]
      ? new URL(fieldData["reward-background"])
      : undefined,
    cta:
      !fieldData["cta-url"] || !fieldData["cta-label"]
        ? undefined
        : {
            label: fieldData["cta-label"],
            description: fieldData["cta-description"] || "",
            url: fieldData["cta-url"],
          },
    rewardSigninRedirectUrl: fieldData["reward-signin-redirect-url"]
      ? new URL(fieldData["reward-signin-redirect-url"])
      : undefined,
  };
};

const toCTATemplate = (
  fieldData: CTAFieldData,
): DiscoverFeedItem | undefined => {
  if (!fieldData["cta-image-url"]) {
    notifyError(
      "CTA template is missing required field: cta-image",
      "discoverFeedError",
    );
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    type: "cta",
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    image: new URL(fieldData["cta-image-url"]),
    title: fieldData["video-title"],
    description: fieldData["cta-description"],
    buttonLabel: fieldData["cta-label"],
    buttonURL: fieldData["cta-url"],
  };
};

const toVideoTemplate = (
  fieldData: VideoFieldData,
): DiscoverFeedItem | undefined => {
  if (!fieldData["video-url"]) {
    notifyError(
      "Video template is missing required field: video-url",
      "discoverFeedError",
    );
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    type: "video",
    title: fieldData["video-title"],
    videoUrl: new VideoUrl(fieldData["video-url"]),
    logoUrl: fieldData["quiz-logo-url-2"]
      ? new URL(fieldData["quiz-logo-url-2"])
      : undefined,
    cta:
      !fieldData["cta-url"] || !fieldData["cta-label"]
        ? undefined
        : {
            description: fieldData["cta-label"],
            url: fieldData["cta-url"],
          },
  };
};

const toQuizTemplate = (
  fieldData: QuizFieldData,
): DiscoverFeedItem | undefined => {
  if (
    !fieldData["quiz-question"] ||
    !fieldData["quiz-answers"] ||
    !fieldData["quiz-correct-answers"] ||
    fieldData["quiz-answers"].split(",").length === 0 ||
    fieldData["quiz-correct-answers"].split(",").length === 0
  ) {
    notifyError(
      "Quiz template is missing required fields",
      "discoverFeedError",
      fieldData,
    );
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    type: "quiz",
    logoUrl: fieldData["quiz-logo-url-2"]
      ? new URL(fieldData["quiz-logo-url-2"])
      : undefined,
    header:
      fieldData["quiz-header-type"] === QuizHeaderToCMSType.Video
        ? {
            type: "video",
            url: new VideoUrl(fieldData["quiz-header-url"]),
          }
        : {
            type: "image",
            initialImage: fieldData["quiz-header-initial-image-url"]
              ? new URL(fieldData["quiz-header-initial-image-url"])
              : undefined,
            answeredImage: fieldData["quiz-header-answered-image-url"]
              ? new URL(fieldData["quiz-header-answered-image-url"])
              : undefined,
          },
    question: fieldData["quiz-question"],
    answers: fieldData["quiz-answers"].split(",").map((a) => a.trim()),
    correctAnswers: fieldData["quiz-correct-answers"]
      ? fieldData["quiz-correct-answers"]
          .split(",")
          .map((a) => Number.parseInt(a.trim()))
      : [],
    correctAnswerMessage: fieldData["quiz-correct-answer-message"],
  };
};

const toPinToHomeTemplate = (
  fieldData: BaseFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "pintohome",
  };
};

const toBonusGemsTemplate = (
  fieldData: BaseFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "bonusgems",
  };
};

const toReferralTemplate = (
  fieldData: BaseFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "referral",
  };
};

const toGetRewardsReadyTemplate = (
  fieldData: BaseFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "getrewardsready",
  };
};

const toFollowGameTemplate = (
  fieldData: FollowGameFieldData,
): DiscoverFeedItem | undefined => {
  if (!fieldData["cta-image-url"]) {
    notifyError(
      "Follow game template is missing required field: cta-image-url",
      "discoverFeedError",
    );
    return undefined;
  }

  const similarGames = fieldData["similar-games"]
    ? fieldData["similar-games"]
        .split(",")
        .map((game) => game.trim())
        .filter((game) => game.length > 0) // Remove empty strings
    : [];

  return {
    ...toBaseTemplate(fieldData),
    type: "followgame",
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    image: fieldData["cta-image-url"],
    title: fieldData["video-title"],
    description: fieldData["cta-description"],
    buttonLabel: fieldData["cta-label"],
    gameLogoUrl: fieldData["quiz-logo-url-2"],
    similarGames,
  };
};

const toSocialQuestTemplate = (
  fieldData: SocialQuestFieldData,
): DiscoverFeedItem | undefined => {
  if (
    !fieldData["cta-image-url"] ||
    !fieldData["cta-label"] ||
    !fieldData["video-title"] ||
    !fieldData["sub-title"] ||
    !fieldData["cta-description"] ||
    !fieldData["quiz-logo-url-2"] ||
    !fieldData["platform-name"]
  ) {
    notifyError(
      "Social Quest template is missing required fields",
      "discoverFeedError",
    );
    return undefined;
  }

  if (
    fieldData["platform-name"] !== "x" &&
    fieldData["platform-name"] !== "discord"
  ) {
    notifyError(
      `Social Quest template has invalid platform-name. Must be x or discord, recieved: ${fieldData["platform-name"]}`,
      "discoverFeedError",
    );
    return undefined;
  }
  return {
    ...toBaseTemplate(fieldData),
    type: "socialquest",
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    backgroundImageUrl: fieldData["cta-image-url"],
    questCaption: fieldData["cta-label"],
    title: fieldData["video-title"],
    subTitle: fieldData["sub-title"], // NEED THIS ONE
    description: fieldData["cta-description"],
    gameLogoUrl: fieldData["quiz-logo-url-2"],
    quest: {
      questId: fieldData["quest-id"] || fieldData.slug,
      gemsEarnable: fieldData["gems-earnable-2"],
      platform: fieldData["platform-name"] as "x" | "discord", // NEED THIS ONE
      linkUrl: fieldData["cta-url"],
    },
  };
};

const toInGameQuestTemplate = (
  fieldData: InGameQuestFieldData,
): DiscoverFeedItem | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "ingamequest",
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    title: fieldData["video-title"],
    description: fieldData["cta-description"],
    buttonLabel: fieldData["cta-label"],
    buttonURL: fieldData["cta-url"],
    backgroundImage: fieldData["cta-image-url"],
  };
};

const templateConverters: TemplateConvertersMap = {
  video: toVideoTemplate,
  quiz: toQuizTemplate,
  cta: toCTATemplate,
  followgame: toFollowGameTemplate,
  reward: toRewardTemplate,
  onboardingsignin: toRewardTemplate,
  pintohome: toPinToHomeTemplate,
  bonusgems: toBonusGemsTemplate,
  referral: toReferralTemplate,
  getrewardsready: toGetRewardsReadyTemplate,
  socialquest: toSocialQuestTemplate,
  ingamequest: toInGameQuestTemplate,
  // These templates are created programmatically and not from the CMS
  allcaughtup: () => undefined,
  selectquestdifficulty: () => undefined,
  socialconnection: () => undefined,
  signin: () => undefined,
  selectgenre: () => undefined,
};

export function convertFieldDataToTemplate<T extends TemplateTypes>(
  questTypeName: T,
  fieldData: FieldDataMap[T],
): DiscoverFeedItem {
  try {
    const converter = templateConverters[questTypeName];
    if (!converter) {
      notifyError(
        `No converter found for quest type: ${questTypeName}`,
        "discoverFeedError",
        questTypeName,
      );
      return undefined;
    }

    // Type assertion is safe here because we've already validated the quest type
    return converter(fieldData);
  } catch (error) {
    notifyError(
      error instanceof Error ? error.message : new Error(String(error)),
      "discoverFeedError",
      error,
    );
    return undefined;
  }
}
